import {
  ArrowLeft,
  Target,
  Dumbbell,
  Timer,
  TrendingUp,
  CheckCircle,
} from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { Badge } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";

const SuggestedWorkouts = () => {
  const navigate = useNavigate();
  const [completedWorkouts, setCompletedWorkouts] = useState<number[]>([]);

  const workouts = [
    {
      id: 1,
      title: "Morning Energy Boost",
      description:
        "Start your day with this energizing routine to kickstart your metabolism",
      duration: "15 min",
      exercises: ["Jumping Jacks", "Push-ups", "Squats", "Plank"],
      calories: "120 cal",
      difficulty: "Beginner",
      icon: "☀️",
      color: "from-accent/20 to-accent/5",
    },
    {
      id: 2,
      title: "Core Crusher",
      description:
        "Targeted ab workout to strengthen your core and improve posture",
      duration: "20 min",
      exercises: [
        "Crunches",
        "Russian Twists",
        "Leg Raises",
        "Mountain Climbers",
      ],
      calories: "180 cal",
      difficulty: "Intermediate",
      icon: "💪",
      color: "from-primary/20 to-primary/5",
    },
    {
      id: 3,
      title: "Cardio Blast",
      description:
        "High-intensity cardio to burn calories and improve cardiovascular health",
      duration: "25 min",
      exercises: ["Burpees", "High Knees", "Jump Rope", "Sprint Intervals"],
      calories: "300 cal",
      difficulty: "Advanced",
      icon: "🔥",
      color: "from-destructive/20 to-destructive/5",
    },
    {
      id: 4,
      title: "Flexibility & Stretch",
      description:
        "Gentle stretching routine to improve flexibility and reduce muscle tension",
      duration: "12 min",
      exercises: [
        "Forward Bend",
        "Cat-Cow",
        "Hip Flexor Stretch",
        "Shoulder Stretch",
      ],
      calories: "60 cal",
      difficulty: "Beginner",
      icon: "🧘",
      color: "from-chart-2/20 to-chart-2/5",
    },
    {
      id: 5,
      title: "Upper Body Strength",
      description:
        "Build strength in your arms, shoulders, and chest with this targeted routine",
      duration: "30 min",
      exercises: ["Dumbbell Press", "Rows", "Bicep Curls", "Tricep Dips"],
      calories: "220 cal",
      difficulty: "Intermediate",
      icon: "💪",
      color: "from-secondary/20 to-secondary/5",
    },
    {
      id: 6,
      title: "Lower Body Power",
      description:
        "Strengthen your legs and glutes with this effective lower body workout",
      duration: "28 min",
      exercises: ["Lunges", "Deadlifts", "Calf Raises", "Leg Press"],
      calories: "250 cal",
      difficulty: "Advanced",
      icon: "🦵",
      color: "from-info/20 to-info/5",
    },
  ];

  const handleComplete = (id: number, title: string) => {
    setCompletedWorkouts([...completedWorkouts, id]);
    toast.success(`Completed: ${title}!`);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "Beginner":
        return "bg-chart-2/10 text-chart-2 border-chart-2/20";
      case "Intermediate":
        return "bg-accent/10 text-accent border-accent/20";
      case "Advanced":
        return "bg-destructive/10 text-destructive border-destructive/20";
      default:
        return "bg-muted/10 text-muted-foreground border-muted/20";
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="sticky top-0 z-10 border-b border-border bg-background">
        <div className="flex items-center gap-3 p-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/home")}
            className="h-9 w-9"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold text-foreground">
            Suggested Workouts
          </h1>
        </div>
      </div>

      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <Card className="rounded-xl border border-border/50 bg-gradient-to-br from-primary/10 to-primary/5 p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/20">
              <Target className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-foreground">
                Personalized For You
              </h2>
              <p className="text-sm text-muted-foreground">
                Based on your fitness level and goals
              </p>
            </div>
          </div>
        </Card>

        {/* Workouts List */}
        <div className="space-y-3">
          {workouts.map((workout, index) => {
            const isCompleted = completedWorkouts.includes(workout.id);
            return (
              <Card
                key={workout.id}
                className={`animate-fade-in rounded-xl border border-border/50 p-5 shadow-sm transition-all hover:shadow-lg ${
                  isCompleted ? "opacity-60" : ""
                }`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div
                  className={`bg-gradient-to-br ${workout.color} relative mb-4 overflow-hidden rounded-lg p-4`}
                >
                  <div className="mb-2 flex items-start justify-between">
                    <div className="text-3xl">{workout.icon}</div>
                    <Badge
                      variant="outline"
                      className={`${getDifficultyColor(workout.difficulty)} backdrop-blur-sm`}
                    >
                      {workout.difficulty}
                    </Badge>
                  </div>
                  <h3 className="mb-1 text-lg font-bold text-foreground">
                    {workout.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {workout.description}
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Timer className="h-4 w-4" />
                      {workout.duration}
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <TrendingUp className="h-4 w-4" />
                      {workout.calories}
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Dumbbell className="h-4 w-4" />
                      {workout.exercises.length} exercises
                    </div>
                  </div>

                  <div className="rounded-lg bg-muted/20 p-3">
                    <p className="mb-2 text-xs font-medium text-muted-foreground">
                      Exercises:
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {workout.exercises.map((exercise, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {exercise}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <Button
                    className="h-10 w-full"
                    onClick={() => handleComplete(workout.id, workout.title)}
                    disabled={isCompleted}
                  >
                    {isCompleted ? (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Completed
                      </>
                    ) : (
                      "Start Workout"
                    )}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Progress Card */}
        <Card className="rounded-xl border border-border/50 bg-card p-5 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-base font-semibold text-foreground">
              Today's Progress
            </h3>
            <CheckCircle className="text-chart-2 h-5 w-5" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Workouts Completed</span>
              <span className="font-semibold text-foreground">
                {completedWorkouts.length} / 3
              </span>
            </div>
            <div className="h-2.5 w-full rounded-full bg-muted/30">
              <div
                className="bg-chart-2 h-2.5 rounded-full transition-all"
                style={{ width: `${(completedWorkouts.length / 3) * 100}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {completedWorkouts.length === 0 &&
                "Start your first workout to begin!"}
              {completedWorkouts.length > 0 &&
                completedWorkouts.length < 3 &&
                "Keep going! You're making great progress."}
              {completedWorkouts.length >= 3 &&
                "Amazing! You've reached your daily goal! 🎉"}
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SuggestedWorkouts;
